// src/lib/firebase.js
// ✅ FULL DROP-IN
// Callables + Punch Clock Model B + Profile/Storage helpers
// Open shifts are derived live from schedules, with shiftClaims storing requests/status metadata.

import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

import { BLOCKS } from "../pages/Schedule/config/scheduleConfig";
import {
  FRONT_9_ASSIGNMENTS,
  BACK_9_ASSIGNMENTS,
} from "../utils/scheduleOrderingUtils";
import {
  normalizeDay,
  ASSIGNMENTS_FIELD,
  AVAILABLE_SHIFTS_FIELD,
  BLOCKED_FIELD,
  EXTRA_SLOTS_FIELD,
  getAutoPublishedShiftsForSlot,
} from "../utils/scheduleDayUtils";

/**
 * Firebase configuration (hard-coded, stable)
 */
const firebaseConfig = {
  apiKey: "AIzaSyDNYKTdBAWBVa9fKY76jFIqT_glIpCgf1M",
  authDomain: "tcmedicalwebportal.firebaseapp.com",
  projectId: "tcmedicalwebportal",
  storageBucket: "tcmedicalwebportal.firebasestorage.app",
  messagingSenderId: "923225243187",
  appId: "1:923225243187:web:1d298f55e4c0c3e5e22d46",
};

// Initialize Firebase once
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Core services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, "us-central1");

export default app;

// Debug helpers
if (typeof window !== "undefined") {
  window.__auth = auth;
  window.__db = db;
  window.__fs = {
    doc,
    getDoc,
    getDocs,
    collection,
    query,
    where,
    orderBy,
    limit,
  };
}

/* ------------------------------------------------------------------ */
/* ERROR NORMALIZATION                                                 */
/* ------------------------------------------------------------------ */

function normalizeCallableError(e, fallback = "Request failed.") {
  const code = e?.code || "unknown";

  let msg =
    e?.details?.message ||
    (typeof e?.details === "string" ? e.details : null) ||
    e?.message ||
    (typeof e === "string" ? e : "") ||
    fallback;

  if (String(msg).trim().toLowerCase() === "internal") {
    const maybe =
      e?.details?.where ||
      e?.details?.error ||
      e?.details?.code ||
      e?.details?.status ||
      e?.message ||
      null;
    if (maybe) msg = `internal (${String(maybe)})`;
  }

  const err = new Error(String(msg));
  err.code = code;
  err.details = e?.details ?? null;
  err.raw = e;
  return err;
}

/* ------------------------------------------------------------------ */
/* CALLABLE WRAPPER                                                    */
/* ------------------------------------------------------------------ */

async function callFn(name, payload = {}) {
  try {
    const user = auth.currentUser;
    if (user) await user.getIdToken(true);

    const fn = httpsCallable(functions, name);
    const res = await fn(payload || {});
    return res?.data || {};
  } catch (e) {
    throw normalizeCallableError(e, `Callable failed: ${name}`);
  }
}

/* ------------------------------------------------------------------ */
/* ROLE MANAGEMENT                                                     */
/* ------------------------------------------------------------------ */

export async function callSetUserRole(arg1, arg2) {
  let uid;
  let role;

  if (typeof arg1 === "object" && arg1 !== null) {
    uid = arg1.uid;
    role = arg1.role;
  } else {
    uid = arg1;
    role = arg2;
  }

  uid = String(uid || "").trim();
  role = String(role || "").trim();

  if (!uid) throw new Error("callSetUserRole: missing uid");
  if (!role) throw new Error("callSetUserRole: missing role");

  const data = await callFn("setUserRole", { uid, role });

  await setDoc(
    doc(db, "users", uid),
    { role, updatedAt: serverTimestamp() },
    { merge: true }
  );

  const snap = await getDoc(doc(db, "users", uid));
  const userDoc = snap.exists() ? snap.data() : null;

  return { ...(data || {}), role, userDoc };
}

/* ------------------------------------------------------------------ */
/* APPLICATION → ACTIVE                                                */
/* ------------------------------------------------------------------ */

export async function callActivateApplicant(payload = {}) {
  const emailKey =
    String(payload.emailKey || "").trim().toLowerCase() ||
    String(payload.email || "").trim().toLowerCase() ||
    String(payload.applicationId || "").trim().toLowerCase();

  const role = String(payload.role || "").trim();

  const normalized = {
    ...payload,
    ...(emailKey ? { emailKey } : {}),
    ...(role ? { role } : {}),
  };

  const data = await callFn("activateApplicant", normalized);
  return data || { ok: true };
}

/* ------------------------------------------------------------------ */
/* NOTIFY APPLICANT(S)                                                 */
/* ------------------------------------------------------------------ */

export async function callNotifyApplicant(payload = {}) {
  const emailKey =
    String(payload.emailKey || "").trim().toLowerCase() ||
    String(payload.email || "").trim().toLowerCase();

  if (!emailKey) throw new Error("callNotifyApplicant: missing emailKey/email");

  const data = await callFn("notifyApplicant", { emailKey });
  return data || { ok: true };
}

export async function callNotifyApplicantsBulk(recipients = []) {
  if (!Array.isArray(recipients) || recipients.length === 0) {
    throw new Error("callNotifyApplicantsBulk: recipients is empty");
  }
  const data = await callFn("notifyApplicantsBulk", { recipients });
  return data || { ok: true };
}

/* ------------------------------------------------------------------ */
/* SEND CUSTOM EMAIL                                                   */
/* ------------------------------------------------------------------ */

export async function callSendCustomEmailBulk(payload = {}) {
  const recipients = Array.isArray(payload.recipients) ? payload.recipients : [];
  const subject = String(payload.subject || "").trim();
  const body = String(payload.body || payload.message || "").trim();

  if (!recipients.length) {
    throw new Error("callSendCustomEmailBulk: recipients is empty");
  }
  if (!subject) throw new Error("callSendCustomEmailBulk: subject is required");
  if (!body) throw new Error("callSendCustomEmailBulk: body is required");

  const data = await callFn("sendCustomEmailBulk", { recipients, subject, body });
  return data || { ok: true };
}

export async function uploadPayrollPdf(filePath, blob) {
  if (!filePath) throw new Error("uploadPayrollPdf: missing filePath");
  if (!blob) throw new Error("uploadPayrollPdf: missing blob");

  const r = ref(storage, filePath);
  await uploadBytes(r, blob, { contentType: "application/pdf" });
  const url = await getDownloadURL(r);
  return { path: filePath, url };
}

export async function callSendPayrollPacketEmail(payload = {}) {
  const data = await callFn("sendPayrollPacketEmail", payload);
  return data || { ok: true };
}

/* ------------------------------------------------------------------ */
/* BOOTSTRAP SUPER USER                                                */
/* ------------------------------------------------------------------ */

export async function callBootstrapSuperUser(payload = {}) {
  const email = String(payload.email || "").trim().toLowerCase();
  const data = await callFn("bootstrapSuperUser", email ? { email } : {});
  return data || { ok: true };
}

/* ------------------------------------------------------------------ */
/* PAYROLL HELPERS                                                     */
/* ------------------------------------------------------------------ */

function toJsDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateIdFromDate(d) {
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseLocalTimeOnDate(dateId, timeText, fallback = "06:00") {
  const datePart = String(dateId || "").trim();
  const timePart = String(timeText || fallback).trim() || fallback;

  const [y, m, d] = datePart.split("-").map((v) => parseInt(v, 10));
  const [hh, mm] = timePart.split(":").map((v) => parseInt(v, 10));

  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
}

function getEmployeePayrollPosition(userData = {}) {
  return String(
    userData.basePosition ||
      userData.providerLevel ||
      userData.position ||
      userData.applicationData?.basePosition ||
      userData.applicationData?.position ||
      ""
  ).trim();
}

function getEmployeeDisplayPosition(userData = {}) {
  return String(
    userData.position ||
      userData.applicationData?.position ||
      ""
  ).trim();
}

function isSupervisorAssignment(roleKey = "", displayLabel = "") {
  const a = String(roleKey || "").toLowerCase();
  const b = String(displayLabel || "").toLowerCase();
  return a.includes("supervisor") || b.includes("supervisor");
}

function getPayRatesDocDefaults() {
  return {
    positions: {},
    supervisorBump: 0,
  };
}

export async function getPayRatesConfig() {
  const snap = await getDoc(doc(db, "appConfig", "payRates"));
  return snap.exists()
    ? { ...getPayRatesDocDefaults(), ...snap.data() }
    : getPayRatesDocDefaults();
}

function getCanonicalPayrollRole(roleKey = "", displayLabel = "") {
  const rk = String(roleKey || "").trim();
  const dl = String(displayLabel || "").trim();

  if (rk.endsWith("__paramedic")) return "Paramedic";
  if (rk.endsWith("__partner")) return "EMT/AEMT/Paramedic";
  if (/^Tent\s+\d+__\d+$/i.test(rk)) return "EMT/AEMT/Paramedic";

  if (/^APRN\/PA(?:\s+|__)?/i.test(rk) || /^APRN\/PA/i.test(dl)) return "APRN/PA";
  if (/^Physician(?:\s+|__)?/i.test(rk) || /^Physician/i.test(dl)) return "Physician";
  if (/^RN(?:\s+|__)?/i.test(rk) || /^RN/i.test(dl)) return "RN";
  if (/^Paramedic(?:\s+|__)?/i.test(rk) || /^Paramedic/i.test(dl)) return "Paramedic";
  if (/^EMT\/AEMT(?:\s+|__)?/i.test(rk) || /^EMT\/AEMT/i.test(dl)) return "EMT/AEMT";
  if (/^Dispatcher/i.test(rk) || /^Dispatcher/i.test(dl)) return "Dispatcher";
  if (/^Tech/i.test(rk) || /^Tech/i.test(dl)) return "Tech";
  if (/^EMR/i.test(rk) || /^EMR/i.test(dl)) return "EMR";
  if (/^EMT/i.test(rk) || /^EMT/i.test(dl)) return "EMT";
  if (/^AEMT/i.test(rk) || /^AEMT/i.test(dl)) return "AEMT";
  if (/^PA/i.test(rk) || /^PA/i.test(dl)) return "PA";
  if (/^APRN/i.test(rk) || /^APRN/i.test(dl)) return "APRN";
  if (/^Resident/i.test(rk) || /^Resident/i.test(dl)) return "Resident";
  if (/^Manager/i.test(rk) || /^Manager/i.test(dl)) return "Manager";
  if (/^Director/i.test(rk) || /^Director/i.test(dl)) return "Director";

  return dl || rk || "";
}

function resolveGroupedRolePosition(groupedRole, userData = {}) {
  const base = getEmployeePayrollPosition(userData);
  const display = getEmployeeDisplayPosition(userData);
  const employeePosition = base || display;

  if (groupedRole === "EMT/AEMT/Paramedic") {
    if (["EMT", "AEMT", "Paramedic"].includes(employeePosition)) {
      return employeePosition;
    }
    return employeePosition || "EMT";
  }

  if (groupedRole === "APRN/PA") {
    if (["APRN", "PA"].includes(employeePosition)) {
      return employeePosition;
    }
    return employeePosition || "APRN";
  }

  return employeePosition;
}

function getRateForPosition(position, payConfig = {}) {
  const raw = payConfig?.positions?.[position];
  return typeof raw === "number" ? Number(raw) : 0;
}

function overlapMs(aStart, aEnd, bStart, bEnd) {
  const start = Math.max(aStart.getTime(), bStart.getTime());
  const end = Math.min(aEnd.getTime(), bEnd.getTime());
  return Math.max(0, end - start);
}

async function findScheduleAssignmentForPunch({ uid, startTime, endTime }) {
  const dateId = formatDateIdFromDate(startTime);
  const scheduleSnap = await getDoc(doc(db, "schedules", dateId));

  if (!scheduleSnap.exists()) {
    return null;
  }

  const day = normalizeDay(scheduleSnap.data() || {});
  const assignmentsBag =
    day[ASSIGNMENTS_FIELD] && typeof day[ASSIGNMENTS_FIELD] === "object"
      ? day[ASSIGNMENTS_FIELD]
      : {};

  const matches = [];

  Object.entries(assignmentsBag).forEach(([roleKey, list]) => {
    if (!Array.isArray(list)) return;

    list.forEach((assignment, index) => {
      const employeeId = String(
        assignment?.employeeId || assignment?.id || ""
      ).trim();

      if (employeeId !== String(uid || "").trim()) return;

      const assignmentStart = parseLocalTimeOnDate(
        dateId,
        assignment?.start || "06:00",
        "06:00"
      );

      const assignmentEnd = parseLocalTimeOnDate(
        dateId,
        assignment?.end || "19:00",
        "19:00"
      );

      const overlap = overlapMs(startTime, endTime, assignmentStart, assignmentEnd);

      matches.push({
        roleKey,
        displayLabel: formatRoleLabel(roleKey),
        roleLabel: getCanonicalPayrollRole(roleKey, formatRoleLabel(roleKey)),
        assignmentId: assignment?.assignmentId || `${roleKey}_${index}`,
        assignmentStart,
        assignmentEnd,
        overlap,
      });
    });
  });

  if (!matches.length) return null;

  matches.sort((a, b) => {
    if (b.overlap !== a.overlap) return b.overlap - a.overlap;
    return a.assignmentStart.getTime() - b.assignmentStart.getTime();
  });

  return matches[0];
}

export async function resolvePayrollForPunch({
  uid,
  startTime,
  endTime,
  userData = null,
  payConfig = null,
}) {
  const resolvedStart = toJsDate(startTime);
  const resolvedEnd = toJsDate(endTime);

  if (!uid) throw new Error("resolvePayrollForPunch: missing uid");
  if (!resolvedStart || !resolvedEnd) {
    throw new Error("resolvePayrollForPunch: missing start/end time");
  }

  const profile =
    userData ||
    (await getMyProfile(uid)) ||
    {};

  const rates = payConfig || (await getPayRatesConfig());

  const scheduleAssignment = await findScheduleAssignmentForPunch({
    uid,
    startTime: resolvedStart,
    endTime: resolvedEnd,
  });

  const assignmentWorked = scheduleAssignment?.roleKey || "";
  const assignmentLabel =
    scheduleAssignment?.displayLabel || "";
  const roleCategory =
    scheduleAssignment?.roleLabel ||
    getEmployeeDisplayPosition(profile) ||
    getEmployeePayrollPosition(profile) ||
    "";

  let payPositionUsed = "";
  let payType = "standard";

  if (scheduleAssignment) {
    const groupedRole = getCanonicalPayrollRole(
      scheduleAssignment.roleKey,
      scheduleAssignment.displayLabel
    );

    if (groupedRole === "EMT/AEMT/Paramedic" || groupedRole === "APRN/PA") {
      payPositionUsed = resolveGroupedRolePosition(groupedRole, profile);
    } else {
      payPositionUsed = groupedRole || getEmployeePayrollPosition(profile);
    }

    if (isSupervisorAssignment(scheduleAssignment.roleKey, scheduleAssignment.displayLabel)) {
      payType = "supervisor";
      const base = getEmployeePayrollPosition(profile) || payPositionUsed;
      payPositionUsed = base;
    }
  } else {
    payPositionUsed =
      getEmployeePayrollPosition(profile) ||
      getEmployeeDisplayPosition(profile) ||
      "";
  }

  const msWorked = Math.max(0, resolvedEnd.getTime() - resolvedStart.getTime());
  const hoursWorked = Number((msWorked / 3600000).toFixed(2));

  let rateUsed = getRateForPosition(payPositionUsed, rates);

  if (payType === "supervisor") {
    rateUsed += Number(rates?.supervisorBump || 0);
  }

  const totalPay = Number((hoursWorked * rateUsed).toFixed(2));

  return {
    assignmentWorked,
    assignmentLabel,
    roleCategory,
    payPositionUsed,
    rateUsed: Number(rateUsed.toFixed(2)),
    payType,
    hoursWorked,
    totalPay,
    payrollSource: scheduleAssignment ? "schedule_assignment" : "employee_profile_fallback",
    payrollDateId: formatDateIdFromDate(resolvedStart),
  };
}

/* ------------------------------------------------------------------ */
/* PUNCH CLOCK — MODEL B                                               */
/* punches/{uid}/entries/{entryId}                                     */
/* ------------------------------------------------------------------ */

export function punchEntriesRef(uid) {
  if (!uid) throw new Error("punchEntriesRef: missing uid");
  return collection(db, "punches", uid, "entries");
}

export function punchEntryRef(uid, entryId) {
  if (!uid) throw new Error("punchEntryRef: missing uid");
  if (!entryId) throw new Error("punchEntryRef: missing entryId");
  return doc(db, "punches", uid, "entries", entryId);
}

export async function getOpenPunch(uid) {
  if (!uid) throw new Error("getOpenPunch: missing uid");

  const qy = query(punchEntriesRef(uid), where("open", "==", true), limit(1));
  const snap = await getDocs(qy);

  if (snap.empty) return null;

  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export async function startPunch(uid, meta = {}) {
  if (!uid) throw new Error("startPunch: missing uid");

  const open = await getOpenPunch(uid);
  if (open) return open;

  const refDoc = await addDoc(punchEntriesRef(uid), {
    open: true,
    startTime: serverTimestamp(),
    endTime: null,
    ...meta,
  });

  const snap = await getDoc(refDoc);
  return { id: refDoc.id, ...snap.data() };
}

export async function endPunch(uid) {
  if (!uid) throw new Error("endPunch: missing uid");

  const open = await getOpenPunch(uid);
  if (!open) return null;

  const refDoc = punchEntryRef(uid, open.id);
  const endAt = new Date();
  const startAt = toJsDate(open.startTime);

  let payrollPatch = {};

  if (startAt) {
    try {
      payrollPatch = await resolvePayrollForPunch({
        uid,
        startTime: startAt,
        endTime: endAt,
      });
    } catch (e) {
      console.error("Payroll snapshot failed during endPunch:", e);
      payrollPatch = {};
    }
  }

  await updateDoc(refDoc, {
    open: false,
    endTime: Timestamp.fromDate(endAt),
    ...payrollPatch,
    payrollCalculatedAt: serverTimestamp(),
  });

  const snap = await getDoc(refDoc);
  return { id: open.id, ...snap.data() };
}

export const clockIn = startPunch;
export const clockOut = endPunch;

/* ------------------------------------------------------------------ */
/* PROFILE                                                             */
/* ------------------------------------------------------------------ */

export async function getMyProfile(uid) {
  if (!uid) throw new Error("getMyProfile: missing uid");
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}

export async function updateMyProfile(uid, patch = {}) {
  if (!uid) throw new Error("updateMyProfile: missing uid");
  await setDoc(
    doc(db, "users", uid),
    { ...patch, updatedAt: serverTimestamp() },
    { merge: true }
  );
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}

export const updateUserProfile = updateMyProfile;

/* ------------------------------------------------------------------ */
/* PIN HASH                                                            */
/* ------------------------------------------------------------------ */

async function sha256Hex(str) {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(str));
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function setUserPin(uid, pin) {
  if (!uid) throw new Error("setUserPin: missing uid");
  const clean = String(pin || "").trim();
  if (clean.length < 4) throw new Error("PIN must be at least 4 digits.");

  const pinHash = await sha256Hex(clean);
  return await updateMyProfile(uid, {
    pinHash,
    pinUpdatedAt: serverTimestamp(),
  });
}

export async function setMyPinWithCallable(uid, pin) {
  try {
    const clean = String(pin || "").trim();
    if (clean.length < 4) throw new Error("PIN must be at least 4 digits.");

    const data = await callFn("setMyPin", { uid, pin: clean });
    if (data?.ok === false) {
      throw new Error(data?.message || "Could not set PIN.");
    }
    return data || { ok: true };
  } catch (e) {
    const code = String(e?.code || "").toLowerCase();
    const msg = String(e?.message || "").toLowerCase();

    const likelyMissingFn =
      code.includes("unimplemented") ||
      code.includes("not-found") ||
      msg.includes("unimplemented") ||
      msg.includes("function") ||
      msg.includes("not found");

    if (likelyMissingFn) {
      await setUserPin(uid, pin);
      return { ok: true, fallback: "client_hash" };
    }

    throw e;
  }
}

/* ------------------------------------------------------------------ */
/* STORAGE                                                             */
/* ------------------------------------------------------------------ */

function safeType(str) {
  return String(str || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]/g, "_");
}

function safeFileName(name) {
  return String(name || "file.jpg").replace(/[^\w.\-]+/g, "_");
}

export async function uploadProfilePhoto(uid, file) {
  if (!uid) throw new Error("uploadProfilePhoto: missing uid");
  if (!file) throw new Error("uploadProfilePhoto: missing file");

  const path = `user_uploads/${uid}/profile/profile.jpg`;
  const r = ref(storage, path);

  await uploadBytes(r, file, { contentType: file.type || "image/jpeg" });
  const url = await getDownloadURL(r);

  await updateMyProfile(uid, { photoURL: url, photoPath: path });
  return url;
}

export async function uploadSignaturePng(uid, pngBlob) {
  if (!uid) throw new Error("uploadSignaturePng: missing uid");
  if (!pngBlob) throw new Error("uploadSignaturePng: missing blob");

  const path = `user_uploads/${uid}/signature/signature.png`;
  const r = ref(storage, path);

  await uploadBytes(r, pngBlob, { contentType: "image/png" });
  const url = await getDownloadURL(r);

  await updateMyProfile(uid, {
    signatureURL: url,
    signaturePath: path,
    signatureUpdatedAt: serverTimestamp(),
  });

  return url;
}

export const uploadSignature = uploadSignaturePng;

export async function uploadCredentialFile(uid, type, ymdOrLabel, file) {
  if (!uid) throw new Error("uploadCredentialFile: missing uid");
  if (!type) throw new Error("uploadCredentialFile: missing type");
  if (!file) throw new Error("uploadCredentialFile: missing file");

  const t = safeType(type);
  const label = String(ymdOrLabel || "").trim() || "no_date";
  const fname = safeFileName(file.name || "credential.jpg");

  const path = `user_uploads/${uid}/credentials/${t}_${label}_${fname}`;
  const r = ref(storage, path);

  await uploadBytes(r, file, { contentType: file.type || "image/jpeg" });
  const url = await getDownloadURL(r);

  return url;
}

/* ------------------------------------------------------------------ */
/* OPEN SHIFTS                                                         */
/* schedules = live source of truth                                    */
/* shiftClaims = requests/status metadata                              */
/* ------------------------------------------------------------------ */

function formatRoleLabel(roleKey) {
  if (!roleKey) return "";

  if (/^Tent\s+\d+__\d+$/i.test(roleKey)) {
    const [tent, slot] = roleKey.split("__");
    return `${tent} - Slot ${slot === "1" ? "1 (Required)" : slot}`;
  }

  if (roleKey.includes("__")) {
    const [base, num] = roleKey.split("__");

    if (base === "Physician") return `Physician ${num}`;
    if (base === "RN") return `RN ${num}`;
    if (base === "APRN/PA") return `APRN/PA ${num}`;
    if (base === "Paramedic") return `Paramedic ${num}`;
    if (base === "EMT/AEMT") return `EMT/AEMT ${num}`;
    if (base.includes("Ambulance")) return `Ambulance ${num}`;

    return base;
  }

  return roleKey;
}

function getCanonicalRoleLabel(roleLabel = "") {
  const raw = String(roleLabel || "").trim();

  if (/^Physician(?:\s+|__)\d+$/i.test(raw)) return "Physician";
  if (/^APRN\/PA(?:\s+|__)\d+$/i.test(raw)) return "APRN/PA";
  if (/^RN(?:\s+|__)\d+$/i.test(raw)) return "RN";
  if (/^Paramedic(?:\s+|__)\d+$/i.test(raw)) return "Paramedic";
  if (/^EMT\/AEMT(?:\s+|__)\d+$/i.test(raw)) return "EMT/AEMT";
  if (/^Ambulance(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";
  if (/^Ambulance\s*\(ALS\)(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";
  if (/^Ambulance\s*\(BLS\)(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";

  return raw;
}

function normalizeShiftTimeText(value = "") {
  const s = String(value || "").trim();

  if (/^\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{4}$/.test(s)) return `${s.slice(0, 2)}:${s.slice(2)}`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(11, 16);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s.slice(11, 16);

  return s;
}

function buildShiftKey(dateId, roleKey, start, end) {
  return [
    String(dateId || "").trim(),
    String(roleKey || "").trim(),
    normalizeShiftTimeText(start),
    normalizeShiftTimeText(end),
  ].join("|");
}

function buildShiftClaimId(dateId, roleKey, start, end) {
  return encodeURIComponent(buildShiftKey(dateId, roleKey, start, end));
}

function buildRoleMetaForDay(day) {
  const meta = new Map();

  const addRole = (roleKey, roleLabel, displayLabel, blockTitle, isExtraSlot = false) => {
    meta.set(roleKey, {
      roleKey,
      roleLabel,
      displayLabel,
      blockTitle,
      isExtraSlot,
    });
  };

  for (const block of BLOCKS) {
    if (block.title === "Front 9") {
      for (const slot of FRONT_9_ASSIGNMENTS) {
        const isCart = /^Cart\s/i.test(slot);
        const isBike = /^Bike\s/i.test(slot);
        const isTent = /^Tent\s+\d+$/i.test(slot);

        if (isCart || isBike) {
          addRole(`${slot}__paramedic`, "Paramedic", "Paramedic", block.title);
          addRole(`${slot}__partner`, "Partner", "Partner", block.title);
        } else if (isTent) {
          [1, 2, 3, 4].forEach((slotNum) => {
            addRole(
              `${slot}__${slotNum}`,
              slot,
              `${slot} - Slot ${slotNum}${slotNum === 1 ? " (Required)" : ""}`,
              block.title
            );
          });
        } else {
          addRole(slot, slot, slot, block.title);
        }
      }
      continue;
    }

    if (block.title === "Back 9") {
      for (const slot of BACK_9_ASSIGNMENTS) {
        const isCart = /^Cart\s/i.test(slot);
        const isBike = /^Bike\s/i.test(slot);
        const isTent = /^Tent\s+\d+$/i.test(slot);

        if (isCart || isBike) {
          addRole(`${slot}__paramedic`, "Paramedic", "Paramedic", block.title);
          addRole(`${slot}__partner`, "Partner", "Partner", block.title);
        } else if (isTent) {
          [1, 2, 3, 4].forEach((slotNum) => {
            addRole(
              `${slot}__${slotNum}`,
              slot,
              `${slot} - Slot ${slotNum}${slotNum === 1 ? " (Required)" : ""}`,
              block.title
            );
          });
        } else {
          addRole(slot, slot, slot, block.title);
        }
      }
      continue;
    }

    for (const slot of block.slots) {
      const isCart = /^Cart\s/i.test(slot);
      const isBike = /^Bike\s/i.test(slot);
      const isTent = /^Tent\s+\d+$/i.test(slot);

      if (isCart || isBike) {
        addRole(`${slot}__paramedic`, "Paramedic", "Paramedic", block.title);
        addRole(`${slot}__partner`, "Partner", "Partner", block.title);
      } else if (isTent) {
        [1, 2, 3, 4].forEach((slotNum) => {
          addRole(
            `${slot}__${slotNum}`,
            slot,
            `${slot} - Slot ${slotNum}${slotNum === 1 ? " (Required)" : ""}`,
            block.title
          );
        });
      } else {
        const canonicalRoleLabel =
          block.title === "Medical HQ" ? getCanonicalRoleLabel(slot) : slot;

        const displayLabel =
          block.title === "Medical HQ" ? formatRoleLabel(slot) : slot;

        addRole(slot, canonicalRoleLabel, displayLabel, block.title);
      }
    }
  }

  for (const [blockTitle, extras] of Object.entries(day[EXTRA_SLOTS_FIELD] || {})) {
    if (!Array.isArray(extras)) continue;

    for (const extra of extras) {
      if (!extra?.id) continue;

      addRole(
        extra.id,
        extra.label || extra.roleType || extra.id,
        extra.label || extra.roleType || extra.id,
        blockTitle,
        true
      );
    }
  }

  return meta;
}

function normalizeOpenShiftPosition(position, roleKey, roleLabel) {
  const p = String(position || "").trim();
  const rk = String(roleKey || "").trim();
  const rl = String(roleLabel || "").trim();

  if (p) return p;
  if (rk.endsWith("__paramedic")) return "Paramedic";
  if (rk.endsWith("__partner")) return "EMT/AEMT/Paramedic";
  if (/^Tent\s+\d+__\d+$/i.test(rk)) return "EMT/AEMT/Paramedic";
  if (/^Dispatcher/i.test(rl) || /^Dispatcher/i.test(rk)) return "Dispatcher";

  return rl || rk || "Open Shift";
}

export function openShiftsRef() {
  return collection(db, "shiftClaims");
}

export function openShiftRef(id) {
  if (!id) throw new Error("openShiftRef: missing id");
  return doc(db, "shiftClaims", id);
}

export async function getAllOpenShifts() {
  const [scheduleSnap, claimSnap] = await Promise.all([
    getDocs(collection(db, "schedules")),
    getDocs(openShiftsRef()),
  ]);

  const claimByKey = new Map();

  claimSnap.docs.forEach((d) => {
    const x = d.data() || {};
    const key = buildShiftKey(x.dateId, x.roleKey, x.start, x.end);

    claimByKey.set(key, {
      id: d.id,
      ...x,
      requests: Array.isArray(x.requests) ? x.requests : [],
    });
  });

  const out = [];

  scheduleSnap.docs.forEach((scheduleDoc) => {
    const dateId = scheduleDoc.id;
    const day = normalizeDay(scheduleDoc.data() || {});
    const roleMeta = buildRoleMetaForDay(day);

    for (const [roleKey, meta] of roleMeta.entries()) {
      const blocked = !!day[BLOCKED_FIELD]?.[roleKey];
      if (blocked) continue;

      const assignments = Array.isArray(day[ASSIGNMENTS_FIELD]?.[roleKey])
        ? day[ASSIGNMENTS_FIELD][roleKey]
        : [];

        if (assignments.length > 0) continue;

      const autoPublished = getAutoPublishedShiftsForSlot({
        roleKey,
        roleLabel: meta.roleLabel,
        blocked,
        assignments,
      });

      const availableMap = day[AVAILABLE_SHIFTS_FIELD] || {};
      const manualPublished = Array.isArray(availableMap[roleKey])
        ? availableMap[roleKey]
        : [];

     const published =
  manualPublished.length > 0 ? manualPublished : autoPublished;

      published.forEach((shift) => {
        const startText = normalizeShiftTimeText(shift.start || "06:00");
        const endText = normalizeShiftTimeText(shift.end || "19:00");
        const key = buildShiftKey(dateId, roleKey, startText, endText);
        const claim = claimByKey.get(key);

        if (String(claim?.status || "").toLowerCase() === "awarded") return;

        const id = claim?.id || buildShiftClaimId(dateId, roleKey, startText, endText);

        const roleLabel =
          shift.roleLabel ||
          meta.displayLabel ||
          meta.roleLabel ||
          roleKey;

        out.push({
          id,
          dateId,
          roleKey,
          roleLabel,
          assignment: roleLabel,
          position: normalizeOpenShiftPosition(shift.position, roleKey, roleLabel),
          start: `${dateId}T${startText}:00`,
          end: `${dateId}T${endText}:00`,
          status: "open",
          isOpen: true,
          requests: Array.isArray(claim?.requests) ? claim.requests : [],
          manual: !!shift.manual,
        });
      });
    }
  });

  out.sort((a, b) => {
    if (a.dateId !== b.dateId) return a.dateId.localeCompare(b.dateId);
    if (a.start !== b.start) return String(a.start).localeCompare(String(b.start));
    return String(a.roleLabel).localeCompare(String(b.roleLabel));
  });

  console.log("FIREBASE getAllOpenShifts count:", out.length);
  return out;
}

export async function requestShift(openShiftId, requestData = {}) {
  if (!openShiftId) throw new Error("requestShift: missing id");

  const shiftRef = openShiftRef(openShiftId);
  const snap = await getDoc(shiftRef);
  const current = snap.exists() ? snap.data() || {} : {};

  const baseShiftData = {
    id: openShiftId,
    dateId: requestData.dateId || current.dateId || "",
    roleKey: requestData.roleKey || current.roleKey || "",
    roleLabel: requestData.roleLabel || current.roleLabel || "",
    position:
      current.position ||
      requestData.positionNeeded ||
      requestData.position ||
      "",
    start: requestData.start || current.start || "",
    end: requestData.end || current.end || "",
    status: "open",
    isOpen: true,
    updatedAt: serverTimestamp(),
    ...(snap.exists() ? {} : { createdAt: serverTimestamp() }),
  };

  const existingRequests = Array.isArray(current.requests)
    ? current.requests
    : [];

  const updatedRequests = existingRequests.some((r) => r.uid === requestData.uid)
    ? existingRequests.map((r) =>
        r.uid === requestData.uid
          ? {
              ...r,
              ...requestData,
              status: "pending",
              requestedAt: r.requestedAt || new Date(),
            }
          : r
      )
    : [
        ...existingRequests,
        {
          ...requestData,
          status: "pending",
          requestedAt: new Date(),
        },
      ];

  await setDoc(
    shiftRef,
    {
      ...baseShiftData,
      requests: updatedRequests,
    },
    { merge: true }
  );

  return {
    ...baseShiftData,
    requests: updatedRequests,
  };
}

export async function denyShiftRequest(openShiftId, requestUid) {
  if (!openShiftId) throw new Error("denyShiftRequest: missing shift id");
  if (!requestUid) throw new Error("denyShiftRequest: missing request uid");

  const shiftRef = openShiftRef(openShiftId);
  const snap = await getDoc(shiftRef);
  const current = snap.exists() ? snap.data() || {} : {};

  const requests = Array.isArray(current.requests) ? current.requests : [];

  const updatedRequests = requests.map((r) =>
    r.uid === requestUid
      ? {
          ...r,
          status: "denied",
          deniedAt: new Date(),
        }
      : r
  );

  await setDoc(
    shiftRef,
    {
      ...current,
      id: openShiftId,
      requests: updatedRequests,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { id: openShiftId, ...current, requests: updatedRequests };
}
export function sortShiftRequests(requests = []) {
  return [...requests].sort((a, b) => {
    const hoursA = Number(a.scheduledHoursAtRequest || 0);
    const hoursB = Number(b.scheduledHoursAtRequest || 0);

    if (hoursA !== hoursB) return hoursA - hoursB;

    const tA =
      a.requestedAt?.seconds
        ? a.requestedAt.seconds * 1000
        : new Date(a.requestedAt || 0).getTime();

    const tB =
      b.requestedAt?.seconds
        ? b.requestedAt.seconds * 1000
        : new Date(b.requestedAt || 0).getTime();

    return tA - tB;
  });
}

export async function awardShift(openShift, request) {
  if (!openShift?.id) throw new Error("awardShift: missing shift");
  if (!request?.uid) throw new Error("awardShift: missing user");

  const isoDate =
    String(openShift.dateId || "").trim() ||
    String(openShift.start || "").slice(0, 10);

  if (!isoDate) throw new Error("awardShift: invalid date");

  const roleKey = String(openShift.roleKey || "").trim();
  if (!roleKey) throw new Error("awardShift: missing roleKey");

  const employeeId = request.uid;
  const refDoc = doc(db, "schedules", isoDate);
  const snap = await getDoc(refDoc);
  const day = normalizeDay(snap.exists() ? snap.data() : {});

  const startText = normalizeShiftTimeText(openShift.start || "06:00");
  const endText = normalizeShiftTimeText(openShift.end || "19:00");

  const current = Array.isArray(day[ASSIGNMENTS_FIELD]?.[roleKey])
  ? day[ASSIGNMENTS_FIELD][roleKey]
  : [];

const assignmentsBag =
  day[ASSIGNMENTS_FIELD] && typeof day[ASSIGNMENTS_FIELD] === "object"
    ? day[ASSIGNMENTS_FIELD]
    : {};

const employeeAlreadyScheduledThatDay = Object.values(assignmentsBag).some((list) =>
  Array.isArray(list) &&
  list.some((a) => String(a.employeeId || "") === String(employeeId))
);

if (employeeAlreadyScheduledThatDay) {
  throw new Error("This employee is already scheduled on this day.");
}

day[ASSIGNMENTS_FIELD] = {
  ...assignmentsBag,
  [roleKey]: [
    ...current,
    {
      assignmentId: `asg_award_${openShift.id}_${employeeId}`,
      employeeId,
      start: startText,
      end: endText,
    },
  ],
};

  day[roleKey] = (day[ASSIGNMENTS_FIELD][roleKey] || []).map((a) => a.employeeId);

  const manual = Array.isArray(day[AVAILABLE_SHIFTS_FIELD]?.[roleKey])
    ? day[AVAILABLE_SHIFTS_FIELD][roleKey]
    : [];

  day[AVAILABLE_SHIFTS_FIELD] = {
    ...(day[AVAILABLE_SHIFTS_FIELD] || {}),
    [roleKey]: manual.filter((s) => {
      const sStart = normalizeShiftTimeText(s.start);
      const sEnd = normalizeShiftTimeText(s.end);
      return !(sStart === startText && sEnd === endText);
    }),
  };

  await setDoc(refDoc, day, { merge: true });

  await setDoc(
    doc(db, "shiftClaims", openShift.id),
    {
      id: openShift.id,
      dateId: isoDate,
      roleKey,
      roleLabel: openShift.roleLabel || "",
      position: openShift.position || "",
      start: openShift.start || `${isoDate}T${startText}:00`,
      end: openShift.end || `${isoDate}T${endText}:00`,
      status: "awarded",
      isOpen: false,
      awardedTo: request.uid,
      awardedToName: request.name || "",
      awardedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}